import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { partQuery } from "#modules/parts/infra/part.query";
import { updatePartSubType } from "#modules/parts/infra/updatePartSubType.api";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import {
  getQuoteRequestLineQueryData,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { queryKeys } from "#shared/queryKeys/queryKeys";

type Context = {
  previousQuoteRequestLine: QuoteRequestLineDto | undefined;
};

type MutationData = {
  newPartSubType: string;
};

export const useUpdatePartSubType = ({
  partId,
  quoteRequestLineId,
}: {
  partId: string;
  quoteRequestLineId: string;
}) => {
  const queryClient = useQueryClient();

  const quoteRequestLineQueryKey = queryKeys.quoteRequestLines.detail(quoteRequestLineId);

  const { mutate, ...rest } = useMutation({
    mutationFn: ({ newPartSubType }) => {
      return updatePartSubType({
        id: partId,
        payload: {
          partSubType: newPartSubType,
          quoteRequestLineId,
        },
      });
    },

    onMutate: async ({ newPartSubType }) => {
      await queryClient.cancelQueries({ queryKey: quoteRequestLineQueryKey });

      const previousQuoteRequestLine = getQuoteRequestLineQueryData(
        queryClient,
        quoteRequestLineId,
      );

      setQuoteRequestLineQueryData(queryClient, quoteRequestLineId, (old) => {
        if (!old) {
          return old;
        }

        return {
          ...old,
          part: { ...old.part, subType: newPartSubType },
        };
      });

      return { previousQuoteRequestLine };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries(partQuery(partId));
      if (result.hasPredictionBeenTriggered) {
        const previousQuoteRequestLine = getQuoteRequestLineQueryData(
          queryClient,
          quoteRequestLineId,
        );

        if (previousQuoteRequestLine) {
          setQuoteRequestLineQueryData(queryClient, quoteRequestLineId, {
            ...previousQuoteRequestLine,
            predictionStatus: "STANDBY",
          });
        }

        void queryClient.invalidateQueries({ queryKey: quoteRequestLineQueryKey });
      }
    },

    onError: (error: Error, _: MutationData, context: Context | undefined) => {
      showErrorToast({ title: error.message });
      errorHandler(error);

      setQuoteRequestLineQueryData(
        queryClient,
        quoteRequestLineId,
        context?.previousQuoteRequestLine,
      );
    },
  });

  return { updatePartSubType: mutate, ...rest };
};
